import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import BIDNavbar from "@/components/feature/BIDNavbar";
import BIDFooter from "@/components/feature/BIDFooter";
import { departments } from "@/mocks/departments";
import { systemsByDeptId } from "@/mocks/zammadStats";
import { fetchLiveStats } from "@/services/zammadService";
import { useAuth } from "@/hooks/useAuth";
import StatsDashboard from "./components/StatsDashboard";
import type { ZammadTicketStats } from "@/mocks/zammadStats";

type DeptFilter = "global" | string;
type SystemFilter = "all" | string;

// ═══════════════════════════════════════════
// Zammad Group Name → Department mapping (mirrors Edge Function DEPT_GROUP_MAP)
// ═══════════════════════════════════════════
const DEPT_GROUP_MAP_FRONTEND: Record<string, string[]> = {
  ti: ["TI", "Mesa de Ayuda", "Soporte TI", "Tecnologia"],
  administracion: ["Administracion", "Administración", "Admin"],
  "bid-tecnologia": ["BID Tecnologia", "BID Tecnología", "BID Tech"],
  contraloria: ["Contraloria", "Contraloría"],
  "diseno-marketing": ["Diseno y Marketing", "Diseño y Marketing", "Marketing", "Diseno", "Diseño"],
  "servicios-generales": ["Servicios Generales"],
  "talento-humano": ["Talento Humano", "Recursos Humanos", "RH"],
  legal: ["Legal", "Juridico", "Jurídico"],
};

const deptOptions: { id: DeptFilter; name: string; icon: string; shortName: string }[] = [
  { id: "global", name: "Todos mis tickets", icon: "ri-user-line", shortName: "Todos" },
  ...departments.map((d) => ({
    id: d.id,
    name: d.name,
    icon: d.icon,
    shortName: d.shortName,
  })),
];

const EMPTY_STATS: ZammadTicketStats = {
  totalTickets: 0,
  openTickets: 0,
  inProgressTickets: 0,
  resolvedTickets: 0,
  closedTickets: 0,
  slaCompliance: 0,
  avgResolutionHours: 0,
  avgFirstResponseMinutes: 0,
  satisfactionAvg: 0,
  ticketsByCategory: [],
  ticketsByPriority: [],
  monthlyTrend: [],
};

export default function EstadisticasPage() {
  const { user, loading: authLoading } = useAuth();
  const [selectedDept, setSelectedDept] = useState<DeptFilter>("global");
  const [selectedSystem, setSelectedSystem] = useState<SystemFilter>("all");
  const [deptDropdownOpen, setDeptDropdownOpen] = useState(false);
  const [liveStats, setLiveStats] = useState<ZammadTicketStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDeptDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Al cambiar de departamento, resetear el sistema
  const handleDeptChange = (dept: DeptFilter) => {
    setSelectedDept(dept);
    setSelectedSystem("all");
    setDeptDropdownOpen(false);
  };

  const currentSystems = systemsByDeptId[selectedDept] || [];
  const showSystems = currentSystems.length > 0;

  // Fetch live stats when filters change
  const fetchStatsForCurrent = useCallback(async () => {
    if (authLoading) return;
    if (!user) {
      setLiveStats(EMPTY_STATS);
      return;
    }

    setIsLoading(true);
    try {
      const stats = await fetchLiveStats({
        dept: selectedDept,
        system: selectedSystem,
        userId: user.id,
        email: user.email,
      });
      setLiveStats(stats);
    } catch (err) {
      console.error("Error fetching live stats:", err);
      setLiveStats(null);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDept, selectedSystem, user, authLoading]);

  useEffect(() => {
    fetchStatsForCurrent();
  }, [fetchStatsForCurrent]);

  const currentStats: ZammadTicketStats = useMemo(() => {
    if (liveStats) return liveStats;
    return EMPTY_STATS;
  }, [liveStats]);

  const selectedDeptInfo = deptOptions.find((d) => d.id === selectedDept);
  const selectedSystemInfo = showSystems
    ? currentSystems.find((s) => s.id === selectedSystem)
    : null;

  const titleLabel = selectedSystemInfo
    ? selectedSystemInfo.name
    : selectedDept === "global"
    ? "Todos mis tickets"
    : selectedDeptInfo?.name || "";

  return (
    <main className="relative min-h-screen bg-background-50">
      <BIDNavbar />

      {/* Page Header */}
      <section className="w-full pt-24 md:pt-28 pb-8 md:pb-10 px-4 md:px-6 lg:px-10 bg-background-100/60">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <a
                  href="/"
                  className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-sm"></i>
                  <span>Inicio</span>
                </a>
                <span className="text-foreground-300">/</span>
                <span className="text-sm text-foreground-700 font-medium">Estadísticas</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-2">
                Estadísticas de mis tickets
              </h1>
              <p className="text-sm text-foreground-600 max-w-xl">
                Métricas calculadas exclusivamente con los tickets donde apareces como solicitante.
              </p>
            </div>

            {/* Sync badge + Diagnostic button */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors ${
                  isLoading
                    ? "bg-amber-50 border border-amber-200/60"
                    : "bg-emerald-50 border border-emerald-200/60"
                }`}
              >
                <div
                  className={`w-1.5 h-1.5 rounded-full ${
                    isLoading
                      ? "bg-amber-500 animate-pulse"
                      : "bg-emerald-500"
                  }`}
                ></div>
                <span
                  className={`text-[11px] font-medium ${
                    isLoading
                      ? "text-amber-700"
                      : "text-emerald-700"
                  }`}
                >
                  {isLoading ? "Cargando datos..." : "Datos actualizados en tiempo real"}
                </span>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Filters — Dropdown + System pills */}
      <section className="w-full py-5 px-4 md:px-6 lg:px-10 border-b border-background-200/50 bg-background-50 sticky top-16 md:top-18 z-40">
        <div className="max-w-7xl mx-auto space-y-3">
          {/* Level 1: Department Dropdown */}
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-foreground-500 flex-shrink-0">Micrositio:</span>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDeptDropdownOpen(!deptDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all bg-accent-500 text-white hover:bg-accent-600"
              >
                <i className={`${selectedDeptInfo?.icon || "ri-global-line"} text-[11px]`}></i>
                <span>{selectedDeptInfo?.name || "Todos mis tickets"}</span>
                <i
                  className={`ri-arrow-down-s-line text-[11px] transition-transform duration-200 ${
                    deptDropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {deptDropdownOpen && (
                <div className="absolute top-full left-0 mt-1.5 w-64 rounded-xl bg-background-50 border border-background-200/80 shadow-lg py-1.5 z-50 max-h-80 overflow-y-auto">
                  {deptOptions.map((opt) => {
                    const isActive = selectedDept === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => handleDeptChange(opt.id)}
                        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-xs font-medium transition-colors cursor-pointer ${
                          isActive
                            ? "bg-accent-50 text-accent-700"
                            : "text-foreground-700 hover:bg-background-100"
                        }`}
                      >
                        <i className={`${opt.icon} text-[13px] ${isActive ? "text-accent-600" : "text-foreground-400"}`}></i>
                        <span>{opt.name}</span>
                        {isActive && (
                          <i className="ri-check-line text-accent-600 text-sm ml-auto"></i>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Level 2: System pills */}
          {showSystems && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-foreground-400 flex-shrink-0">Sistema:</span>
              <div className="flex items-center gap-1.5 flex-wrap">
                <button
                  onClick={() => setSelectedSystem("all")}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap cursor-pointer transition-all ${
                    selectedSystem === "all"
                      ? "bg-foreground-900 text-white"
                      : "text-foreground-600 hover:text-foreground-900 hover:bg-background-100 border border-background-200/70"
                  }`}
                >
                  Todos
                </button>
                {currentSystems.map((sys) => {
                  const isActive = selectedSystem === sys.id;
                  return (
                    <button
                      key={sys.id}
                      onClick={() => setSelectedSystem(sys.id)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap cursor-pointer transition-all ${
                        isActive
                          ? "bg-accent-500 text-white"
                          : "text-foreground-600 hover:text-foreground-900 hover:bg-background-100 border border-background-200/70"
                      }`}
                    >
                      <i className={`${sys.icon} text-[10px]`}></i>
                      <span className="hidden sm:inline">{sys.name}</span>
                      <span className="sm:hidden">{sys.shortName}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Stats Dashboard */}
      <section className="w-full py-8 md:py-10 px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <StatsDashboard stats={currentStats} title={titleLabel} />
        </div>
      </section>

      <BIDFooter />
    </main>
  );
}
